import React from 'react';
import type { QuarantinedAgent, AppealHistory } from '../types';
import { formatCurrency, formatDate, formatDid, formatRelativeTime } from '../utils/format';

export interface QuarantineZoneProps {
  quarantined: QuarantinedAgent[];
  appealHistory?: AppealHistory[];
  className?: string;
  onFileAppeal?: (agentDid: string) => void;
  onViewDetails?: (agentDid: string) => void;
}

/**
 * Quarantine & Appeals Panel
 * View quarantined agents and manage appeals
 */
export function QuarantineZone({
  quarantined,
  appealHistory = [],
  className = '',
  onFileAppeal,
  onViewDetails,
}: QuarantineZoneProps) {
  const recentAppeals = appealHistory.slice(0, 5);

  return (
    <div className={`bg-gray-900 border border-gray-800 rounded-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-white">Quarantine Zone</h2>
        {quarantined.length > 0 && (
          <span className="px-3 py-1 text-xs bg-red-900/50 text-red-300 rounded-full animate-pulse">
            {quarantined.length} Agent{quarantined.length !== 1 ? 's' : ''} Quarantined
          </span>
        )}
      </div>

      {/* Quarantined Agents */}
      {quarantined.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-gray-500">
          <span className="text-green-400 mr-2">✓</span>
          No agents currently quarantined
        </div>
      ) : (
        <div className="space-y-3 mb-6">
          {quarantined.map((agent) => (
            <QuarantinedAgentCard
              key={agent.agentDid}
              agent={agent}
              onFileAppeal={onFileAppeal}
              onViewDetails={onViewDetails}
            />
          ))}
        </div>
      )}

      {/* Appeal History */}
      {recentAppeals.length > 0 && (
        <div className="border-t border-gray-800 pt-4">
          <div className="text-sm text-gray-400 mb-3">Appeal History</div>
          <div className="space-y-2">
            {recentAppeals.map((appeal) => (
              <AppealRow key={appeal.id} appeal={appeal} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface QuarantinedAgentCardProps {
  agent: QuarantinedAgent;
  onFileAppeal?: (agentDid: string) => void;
  onViewDetails?: (agentDid: string) => void;
}

function QuarantinedAgentCard({
  agent,
  onFileAppeal,
  onViewDetails,
}: QuarantinedAgentCardProps) {
  const deadline = new Date(agent.appealDeadline);
  const now = new Date();
  const hoursRemaining = Math.max(0, (deadline.getTime() - now.getTime()) / (1000 * 60 * 60));
  const canAppeal = agent.appealStatus === 'none' && hoursRemaining > 0;

  return (
    <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-4">
      {/* Top Row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <code className="text-sm text-gray-300 font-mono">{formatDid(agent.agentDid, 8)}</code>
        </div>
        <span className="px-2 py-0.5 text-xs bg-red-900/50 text-red-300 rounded">
          {agent.reason}
        </span>
      </div>

      {/* Details */}
      <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
        <div>
          <span className="text-gray-500">Quarantined:</span>{' '}
          <span className="text-gray-300">{formatRelativeTime(agent.quarantinedAt)}</span>
        </div>
        <div>
          <span className="text-gray-500">Bond at Risk:</span>{' '}
          <span className="text-red-400">{formatCurrency(agent.bondAtRisk)}</span>
        </div>
        <div>
          <span className="text-gray-500">Appeal Deadline:</span>{' '}
          <span className={hoursRemaining < 24 ? 'text-yellow-400' : 'text-gray-300'}>
            {hoursRemaining.toFixed(0)}h remaining
          </span>
        </div>
        <div>
          <span className="text-gray-500">Appeal Status:</span>{' '}
          <span className={
            agent.appealStatus === 'granted' ? 'text-green-400' :
            agent.appealStatus === 'denied' ? 'text-red-400' :
            agent.appealStatus === 'pending' ? 'text-yellow-400' :
            'text-gray-400'
          }>
            {agent.appealStatus === 'none' ? 'Not filed' : agent.appealStatus.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {canAppeal && onFileAppeal && (
          <button
            onClick={() => onFileAppeal(agent.agentDid)}
            className="px-4 py-1.5 text-sm bg-purple-600 hover:bg-purple-500 text-white rounded transition-colors"
          >
            File Appeal
          </button>
        )}
        {onViewDetails && (
          <button
            onClick={() => onViewDetails(agent.agentDid)}
            className="px-4 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
          >
            View Details
          </button>
        )}
      </div>
    </div>
  );
}

interface AppealRowProps {
  appeal: AppealHistory;
}

function AppealRow({ appeal }: AppealRowProps) {
  const isGranted = appeal.outcome === 'granted';

  return (
    <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
      <div className="flex items-center gap-3">
        <span className="text-gray-500 text-sm">{formatDate(appeal.resolvedAt)}</span>
        <code className="text-xs text-gray-400 font-mono">{formatDid(appeal.agentDid, 6)}</code>
        <span className="px-2 py-0.5 text-xs bg-gray-700 text-gray-300 rounded">
          {appeal.reason}
        </span>
      </div>
      <div className="flex items-center gap-4 text-sm">
        <span className={isGranted ? 'text-green-400' : 'text-red-400'}>
          APPEAL {appeal.outcome.toUpperCase()}
        </span>
        {isGranted && appeal.bondRefunded > 0 && (
          <span className="text-gray-400">
            {formatCurrency(appeal.bondRefunded)} refunded
          </span>
        )}
        <span className="text-gray-500 text-xs">
          Validators: {appeal.validatorVotes}
        </span>
      </div>
    </div>
  );
}
